import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '~/shadcn/components/ui/Button';
import { Alert } from '~/shadcn/components/ui/Alert';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

const BulkMovePalletsSummary = ({ moveResults, onClose }) => {
    const [showSuccessDetails, setShowSuccessDetails] = useState(false);
    const [showFailureDetails, setShowFailureDetails] = useState(false);

    const successes = moveResults.data?.successes || {};
    const failures = moveResults.data?.failures || {};
    const numSuccesses = Object.keys(successes).length;
    const numFailures = Object.keys(failures).length;

    return (
        <div className="p-6 flex flex-col items-center gap-6">
            <div className="w-full space-y-4">
                {numSuccesses > 0 && (
                    <div className="space-y-2">
                        <Alert variant="default" className="bg-green-50 border-green-200 text-green-500">
                            <CheckCircle2 className="h-4 w-4" />
                            <p className="text-xs text-green-800">
                                <span className="font-semibold">Success:</span> {numSuccesses} Pallet{numSuccesses !== 1 ? 's' : ''} moved successfully
                            </p>
                        </Alert>
                        <div className="ml-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-green-800 p-0 h-auto hover:bg-transparent hover:text-green-900 flex items-center gap-1"
                                onClick={() => setShowSuccessDetails(!showSuccessDetails)}
                            >
                                {showSuccessDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                {showSuccessDetails ? 'Hide Details' : 'Show Details'}
                            </Button>
                            {showSuccessDetails && (
                                <div className="mt-2 text-xs text-green-800">
                                    <p className="font-semibold mb-1">Pallet IDs:</p>
                                    <ul className="list-disc pl-4">
                                        {Object.keys(successes).map((palletId) => (
                                            <li key={palletId}>Pallet {palletId}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {numFailures > 0 && (
                    <div className="space-y-2">
                        <Alert variant="default" className="bg-red-50 border-red-200 text-red-500">
                            <XCircle className="h-4 w-4" />
                            <p className="text-xs text-red-800">
                                <span className="font-semibold">Failed:</span> {numFailures} Pallet{numFailures !== 1 ? 's' : ''} failed to move
                            </p>
                        </Alert>
                        <div className="ml-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-red-800 p-0 h-auto hover:bg-transparent hover:text-red-900 flex items-center gap-1"
                                onClick={() => setShowFailureDetails(!showFailureDetails)}
                            >
                                {showFailureDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                {showFailureDetails ? 'Hide Details' : 'Show Details'}
                            </Button>
                            {showFailureDetails && (
                                <div className="mt-2 text-xs text-red-800">
                                    <p className="font-semibold mb-1">Failed Pallets:</p>
                                    <ul className="list-disc pl-4 space-y-2">
                                        {Object.entries(failures).map(([palletId, errorMessage]) => (
                                            <li key={palletId}>
                                                <span className="font-medium">Pallet {palletId}:</span> {errorMessage}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Button variant="default" onClick={onClose}>
                Close
            </Button>
        </div>
    );
};

BulkMovePalletsSummary.propTypes = {
    moveResults: PropTypes.shape({
        msg: PropTypes.string,
        data: PropTypes.shape({
            successes: PropTypes.object,
            failures: PropTypes.object
        })
    }).isRequired,
    onClose: PropTypes.func.isRequired
};

export { BulkMovePalletsSummary };
